import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getApiBaseUrl } from '../services/apiClient';
import { FieldLabel } from '../components/FieldLabel';
import { toTurkishUpperCase } from '../utils/turkishCase';
import { formatChecklistCevapGosterim } from '../utils/checklistDisplay';

const formatFiyat = (n) => {
  if (n == null || n === '' || isNaN(Number(n))) return '-';
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(n));
};

const getIslemTipiOzeti = (islemTipi) => {
  const t = String(islemTipi || '').trim().toLowerCase();
  if (t.includes('kurulum')) return { etiket: 'kurulum' };
  if (t.includes('bakım') || t.includes('bakim')) return { etiket: 'bakım' };
  return null;
};

const hasCihazOzeti = (cihazOzeti) => {
  if (!cihazOzeti) return false;
  return Boolean(
    cihazOzeti.cihazAdi
    || cihazOzeti.seriNumarasi
    || cihazOzeti.garantiBaslangic
    || cihazOzeti.garantiBitis,
  );
};

export default function RandevuDetayPublicPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = 'ÜNMAK | Servis Kaydı Randevu Detayı';
    return () => { document.title = 'SM Yönetim Sistemi'; };
  }, []);

  useEffect(() => {
    if (!token) {
      setError('Geçersiz link');
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        const url = `${getApiBaseUrl()}/api/public/randevu-detay?token=${encodeURIComponent(token)}`;
        const res = await fetch(url);
        const json = await res.json();

        if (!res.ok) {
          setError(json.mesaj || 'Sayfa yüklenemedi');
          return;
        }

        if (json.basarili && json.veri) {
          setData(json.veri);
        } else {
          setError('Veri bulunamadı');
        }
      } catch (err) {
        setError('Sayfa yüklenirken bir hata oluştu');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  if (loading) {
    return (
      <div style={styles.page} className="randevu-detay-public">
        <div style={styles.loading}>
          <div style={styles.loadingSpinner} />
          <span>Yükleniyor...</span>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={styles.page} className="randevu-detay-public">
        <div style={styles.errorCard}>
          <div style={styles.errorIcon}>!</div>
          <div style={styles.errorText}>{error || 'Geçersiz veya süresi dolmuş link'}</div>
        </div>
      </div>
    );
  }

  const showFiyat = !data.garantiDahilinde;
  const sartSaglanmadi = data.garantiSartlariSaglanmadi === true;
  const teknisyenAdi = [data.teknisyen?.adi, data.teknisyen?.soyadi].filter(Boolean).join(' ');
  const musteriAdSoyad = [data.musteri?.ad, data.musteri?.soyad].filter(Boolean).join(' ');
  const islemTipiOzeti = getIslemTipiOzeti(data.islemTipi);
  const hideYapilanIslemler = Boolean(islemTipiOzeti);
  const showCihazOzeti = hasCihazOzeti(data.cihazOzeti);
  const checklistSorular = data.checklistOzeti?.sorularVeCevaplar || [];
  const checklistBaslik = data.checklistOzeti?.checklistAd || 'Kontrol Listesi';

  return (
    <div style={styles.page} className="randevu-detay-public">
      <div style={styles.card} className="randevu-detay-card">
        {/* Header: Logo + Ünmak Bilgileri */}
        <div style={styles.header} className="randevu-detay-header">
          <div style={styles.headerLogo}>
            <img
              src="/assets/img/logo.png"
              alt="Ünmak"
              style={styles.logo}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
          <div style={styles.headerCompany} className="randevu-detay-header-company">
            <div style={styles.companyName} className="randevu-detay-company-name">ÜNLÜSOY ISITMA SİSTEMLERİ SANAYİ VE TİCARET A.Ş.</div>
            <div style={styles.companyAddress} className="randevu-detay-company-address">İzmir Pancar Organize Sanayi Bölgesi (İPOSB) 10. Cadde No: 2 Pancar, 35860 Torbalı / İZMİR, Türkiye</div>
            <div style={styles.companyContact} className="randevu-detay-company-contact">
              <span>444 35 32</span>
              <span style={styles.companySeparator} className="randevu-detay-company-separator">|</span>
              <span>+90 232 469 24 12 (fax)</span>
            </div>
          </div>
        </div>

        {/* Başlık */}
        <h1 style={styles.mainTitle} className="randevu-detay-main-title">Servis Kaydı Randevu Detayı</h1>

        {sartSaglanmadi && data.garantiSartlariUyarisi ? (
          <div style={styles.alertBox} className="randevu-detay-alert">
            <span style={styles.alertIcon}>⚠️</span>
            <span>{data.garantiSartlariUyarisi}</span>
          </div>
        ) : null}

        {/* Bilgi Kartları */}
        <div style={styles.infoGrid} className="randevu-detay-info-grid">
          <div style={styles.infoCard} className="randevu-detay-info-card">
            <FieldLabel style={styles.infoCardLabel} as="div">Randevu Bilgileri</FieldLabel>
            <div style={styles.infoCardContent}>
              <div style={styles.infoRow}>
                <span style={styles.infoIcon}>📅</span>
                <span><strong>Tarih:</strong> {data.randevuBilgileri?.randevuTarihi || '-'}</span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoIcon}>🕐</span>
                <span><strong>Saat:</strong> {data.randevuBilgileri?.randevuSaati || '-'}</span>
              </div>
            </div>
            <div style={styles.infoCardSectionDivider} />
            <FieldLabel style={styles.infoCardLabel} as="div">İşlem Bilgileri</FieldLabel>
            <div style={styles.infoCardContent}>
              <div style={styles.infoRow}>
                <span style={styles.infoIcon}>📅</span>
                <span><strong>Tarih:</strong> {data.islemBilgileri?.islemTarihi || '-'}</span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoIcon}>🕐</span>
                <span><strong>Saat:</strong> {data.islemBilgileri?.islemSaati || '-'}</span>
              </div>
            </div>
          </div>
          <div style={styles.infoCard} className="randevu-detay-info-card">
            <FieldLabel style={styles.infoCardLabel} as="div">Müşteri Bilgileri</FieldLabel>
            <div style={styles.infoCardContent}>
              <div style={styles.infoRow}>
                <span style={styles.infoIcon}>👤</span>
                <span><strong>Ad Soyad:</strong> {musteriAdSoyad || '-'}</span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoIcon}>📞</span>
                <span><strong>Telefon:</strong> {data.musteri?.telefon || '-'}</span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoIcon}>📍</span>
                <span><strong>Adres:</strong> {data.musteri?.adres || '-'}</span>
              </div>
            </div>
          </div>
          <div style={styles.infoCard} className="randevu-detay-info-card">
            <FieldLabel style={styles.infoCardLabel} as="div">Teknik Servis</FieldLabel>
            <div style={styles.infoCardContent}>
              <div style={styles.infoRow}>
                <span style={styles.infoIcon}>🏢</span>
                <span>{data.teknikServis?.ad || '-'}</span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoIcon}>📞</span>
                <span>{data.teknikServis?.telefon || '-'}</span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoIcon}>📍</span>
                <span>{data.teknikServis?.adres || '-'}</span>
              </div>
            </div>
          </div>
          <div style={styles.infoCard} className="randevu-detay-info-card">
            <FieldLabel style={styles.infoCardLabel} as="div">Uygulayan Teknisyen</FieldLabel>
            <div style={styles.infoCardContent}>
              <div style={styles.infoRow}>
                <span style={styles.infoIcon}>🔧</span>
                <span>{teknisyenAdi || '-'}</span>
              </div>
            </div>
          </div>
        </div>

        {showCihazOzeti ? (
          <div style={{ ...styles.infoCard, ...styles.cihazCard }} className="randevu-detay-info-card randevu-detay-cihaz-card">
            <FieldLabel style={styles.infoCardLabel} as="div">Cihaz Bilgileri</FieldLabel>
            <div style={styles.infoCardContent}>
              <div style={styles.infoRow}>
                <span style={styles.infoIcon}>📦</span>
                <span><strong>Cihaz:</strong> {data.cihazOzeti.cihazAdi || '—'}</span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoIcon}>🔢</span>
                <span><strong>Seri No:</strong> {data.cihazOzeti.seriNumarasi || '—'}</span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoIcon}>📅</span>
                <span><strong>Garanti Başlangıç:</strong> {data.cihazOzeti.garantiBaslangic || '—'}</span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoIcon}>📅</span>
                <span><strong>Garanti Bitiş:</strong> {data.cihazOzeti.garantiBitis || '—'}</span>
              </div>
            </div>
          </div>
        ) : null}

        {islemTipiOzeti ? (
          <div style={styles.islemTipiCard} className="randevu-detay-islem-tipi-card">
            Bu randevuda <strong>{islemTipiOzeti.etiket}</strong> işlemi yapılmıştır.
          </div>
        ) : null}

        {checklistSorular.length > 0 ? (
          <div style={styles.section} className="randevu-detay-section">
            <div style={styles.sectionHeader} className="randevu-detay-section-header">
              <span style={styles.sectionIcon}>✅</span>
              <span>{checklistBaslik}</span>
            </div>
            <table style={styles.table} className="randevu-detay-table">
              <thead>
                <tr>
                  <th style={styles.thCenter}>{toTurkishUpperCase('#')}</th>
                  <th style={styles.th}>{toTurkishUpperCase('Soru')}</th>
                  <th style={styles.thCenter}>{toTurkishUpperCase('Cevap')}</th>
                </tr>
              </thead>
              <tbody>
                {checklistSorular.map((row, i) => (
                  <tr key={i} style={i % 2 === 1 ? styles.trStriped : {}}>
                    <td style={styles.tdCenter}>{i + 1}</td>
                    <td style={styles.td}>{row.metin || '—'}</td>
                    <td style={styles.tdCenter}>{formatChecklistCevapGosterim(row)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {/* Yapılan İşlemler */}
        {!sartSaglanmadi && !hideYapilanIslemler && data.yapilanIslemler?.length > 0 && (
          <div style={styles.section} className="randevu-detay-section">
            <div style={styles.sectionHeader} className="randevu-detay-section-header">
              <span style={styles.sectionIcon}>⚙️</span>
              <span>Yapılan İşlemler</span>
            </div>
            <table style={styles.table} className="randevu-detay-table">
              <thead>
                <tr>
                  <th style={styles.th}>{toTurkishUpperCase('İşlem')}</th>
                  {showFiyat && <th style={styles.thRight}>{toTurkishUpperCase('Fiyat')}</th>}
                </tr>
              </thead>
              <tbody>
                {data.yapilanIslemler.map((row, i) => (
                  <tr key={i} style={i % 2 === 1 ? styles.trStriped : {}}>
                    <td style={styles.td}>{row.islemAdi || '-'}</td>
                    {showFiyat && <td style={styles.tdRight}>{formatFiyat(row.fiyat)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* İlave İşlemler */}
        {!sartSaglanmadi && data.ilaveIslemler?.length > 0 && (
          <div style={styles.section} className="randevu-detay-section">
            <div style={styles.sectionHeader} className="randevu-detay-section-header">
              <span style={styles.sectionIcon}>➕</span>
              <span>İlave İşlemler</span>
            </div>
            <table style={styles.table} className="randevu-detay-table">
              <thead>
                <tr>
                  <th style={styles.th}>{toTurkishUpperCase('İşlem')}</th>
                  <th style={styles.thCenter}>{toTurkishUpperCase('Adet')}</th>
                  {showFiyat && <th style={styles.thRight}>{toTurkishUpperCase('Fiyat')}</th>}
                </tr>
              </thead>
              <tbody>
                {data.ilaveIslemler.map((row, i) => (
                  <tr key={i} style={i % 2 === 1 ? styles.trStriped : {}}>
                    <td style={styles.td}>{row.islemAdi || '-'}</td>
                    <td style={styles.tdCenter}>{row.adet ?? 1}</td>
                    {showFiyat && <td style={styles.tdRight}>{formatFiyat(row.fiyat)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Değiştirilen Parçalar */}
        {!sartSaglanmadi && data.degistirilenParcalar?.length > 0 && (
          <div style={styles.section} className="randevu-detay-section">
            <div style={styles.sectionHeader} className="randevu-detay-section-header">
              <span style={styles.sectionIcon}>🔧</span>
              <span>Değiştirilen Parçalar</span>
            </div>
            <table style={styles.table} className="randevu-detay-table">
              <thead>
                <tr>
                  <th style={styles.th}>{toTurkishUpperCase('Parça')}</th>
                  <th style={styles.thCenter}>{toTurkishUpperCase('Adet')}</th>
                  {showFiyat && <th style={styles.thRight}>{toTurkishUpperCase('Fiyat')}</th>}
                </tr>
              </thead>
              <tbody>
                {data.degistirilenParcalar.map((row, i) => (
                  <tr key={i} style={i % 2 === 1 ? styles.trStriped : {}}>
                    <td style={styles.td}>{row.parcaAdi || '-'}</td>
                    <td style={styles.tdCenter}>{row.adet ?? 1}</td>
                    {showFiyat && <td style={styles.tdRight}>{formatFiyat(row.fiyat)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* İlave Parçalar */}
        {data.ilaveParcalar?.length > 0 && (
          <div style={styles.section} className="randevu-detay-section">
            <div style={styles.sectionHeader} className="randevu-detay-section-header">
              <span style={styles.sectionIcon}>📦</span>
              <span>İlave Parçalar</span>
            </div>
            <table style={styles.table} className="randevu-detay-table">
              <thead>
                <tr>
                  <th style={styles.th}>{toTurkishUpperCase('Parça')}</th>
                  <th style={styles.thCenter}>{toTurkishUpperCase('Adet')}</th>
                </tr>
              </thead>
              <tbody>
                {data.ilaveParcalar.map((row, i) => (
                  <tr key={i} style={i % 2 === 1 ? styles.trStriped : {}}>
                    <td style={styles.td}>{row.parcaAdi || '-'}</td>
                    <td style={styles.tdCenter}>{row.adet ?? 1}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Randevu bedeli / Toplam */}
        {(sartSaglanmadi || showFiyat || data.randevuBedeliGosterim?.toplamTutar != null) ? (
          <div style={styles.toplamTutarSection} className="randevu-detay-toplam">
            <div style={styles.toplamTutarLabel}>Randevu Bedeli</div>
            <div style={styles.toplamTutarValue} className="randevu-detay-toplam-value">
              {formatFiyat(
                data.randevuBedeliGosterim?.toplamTutar
                  ?? (sartSaglanmadi ? (data.servisBedeli ?? data.toplamTutar) : data.toplamTutar)
                  ?? 0,
              )}
            </div>
            {data.randevuBedeliGosterim?.altEtiket ? (
              <div style={{ fontSize: '13px', color: '#64748b', marginTop: '6px', textAlign: 'right' }}>
                {data.randevuBedeliGosterim.altEtiket}
              </div>
            ) : null}
          </div>
        ) : null}

        {/* İşlem Fotoğrafları */}
        {data.fotoUrls && data.fotoUrls.length > 0 && (
          <div style={styles.section} className="randevu-detay-section">
            <div style={styles.sectionHeader} className="randevu-detay-section-header">İşlem Fotoğrafları</div>
            <div style={styles.photosGrid}>
              {data.fotoUrls.map((url, idx) => {
                const fullUrl = url.startsWith('http') ? url : `${getApiBaseUrl()}${url.startsWith('/') ? '' : '/'}${url}`;
                return (
                  <a key={idx} href={fullUrl} target="_blank" rel="noopener noreferrer" style={styles.photoLink}>
                    <img src={fullUrl} alt={`İşlem ${idx + 1}`} style={styles.photoImg} onError={(e) => { e.target.style.display = 'none'; }} />
                  </a>
                );
              })}
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={styles.footer}>
          <div style={styles.footerLine} />
          <div style={styles.footerText}>Bu belge elektronik ortamda oluşturulmuştur.</div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
    padding: '16px 12px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  card: {
    width: '100%',
    maxWidth: '900px',
    background: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)',
    padding: '20px 16px',
    marginBottom: '24px',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '32px',
    marginBottom: '24px',
    paddingBottom: '24px',
    borderBottom: '2px solid #e2e8f0',
  },
  headerLogo: {
    flexShrink: 0,
  },
  logo: {
    height: '64px',
    width: 'auto',
  },
  headerCompany: {
    flex: 1,
    textAlign: 'right',
  },
  companyName: {
    fontSize: '13px',
    fontWeight: 700,
    color: '#1e293b',
    marginBottom: '6px',
    lineHeight: 1.4,
  },
  companyAddress: {
    fontSize: '11px',
    color: '#64748b',
    lineHeight: 1.5,
    marginBottom: '6px',
  },
  companyContact: {
    fontSize: '12px',
    color: '#334155',
    fontWeight: 500,
  },
  companySeparator: {
    margin: '0 8px',
    color: '#cbd5e1',
  },
  mainTitle: {
    margin: '0 0 28px',
    fontSize: '22px',
    fontWeight: 700,
    color: '#1e293b',
    letterSpacing: '-0.02em',
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
    marginBottom: '32px',
  },
  infoCard: {
    background: '#f8fafc',
    borderRadius: '10px',
    padding: '20px 24px',
    border: '1px solid #e2e8f0',
  },
  infoCardLabel: {
    fontSize: '11px',
    fontWeight: 700,
    color: '#64748b',
    letterSpacing: '0.08em',
    marginBottom: '12px',
  },
  infoCardContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  infoCardSectionDivider: {
    marginTop: '16px',
    marginBottom: '12px',
    borderTop: '1px solid #e2e8f0',
  },
  islemTipiCard: {
    marginBottom: '28px',
    padding: '20px 24px',
    background: '#f8fafc',
    borderRadius: '10px',
    border: '1px solid #e2e8f0',
    fontSize: '15px',
    color: '#334155',
    lineHeight: 1.5,
    textAlign: 'center',
  },
  cihazCard: {
    marginBottom: '28px',
  },
  infoRow: {
    fontSize: '14px',
    color: '#334155',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    lineHeight: 1.5,
  },
  infoIcon: {
    fontSize: '14px',
    flexShrink: 0,
  },
  section: {
    marginBottom: '28px',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '15px',
    fontWeight: 700,
    color: '#1e293b',
    marginBottom: '12px',
    paddingBottom: '8px',
  },
  photosGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    gap: '12px',
  },
  photoLink: {
    display: 'block',
    borderRadius: '8px',
    overflow: 'hidden',
    border: '1px solid #e2e8f0',
    background: '#f8fafc',
  },
  photoImg: {
    width: '100%',
    height: '160px',
    objectFit: 'cover',
    display: 'block',
  },
  sectionIcon: {
    fontSize: '16px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
    borderRadius: '8px',
    overflow: 'hidden',
    border: '1px solid #e2e8f0',
  },
  th: {
    padding: '14px 18px',
    textAlign: 'left',
    backgroundColor: '#1e293b',
    color: '#ffffff',
    fontWeight: 600,
    fontSize: '12px',
    letterSpacing: '0.05em',
  },
  thCenter: {
    padding: '14px 18px',
    textAlign: 'center',
    backgroundColor: '#1e293b',
    color: '#ffffff',
    fontWeight: 600,
    fontSize: '12px',
    letterSpacing: '0.05em',
  },
  thRight: {
    padding: '14px 18px',
    textAlign: 'right',
    backgroundColor: '#1e293b',
    color: '#ffffff',
    fontWeight: 600,
    fontSize: '12px',
    letterSpacing: '0.05em',
  },
  td: {
    padding: '14px 18px',
    borderBottom: '1px solid #f1f5f9',
    color: '#334155',
  },
  tdCenter: {
    padding: '14px 18px',
    textAlign: 'center',
    borderBottom: '1px solid #f1f5f9',
    color: '#334155',
  },
  tdRight: {
    padding: '14px 18px',
    textAlign: 'right',
    borderBottom: '1px solid #f1f5f9',
    color: '#334155',
    fontWeight: 500,
  },
  trStriped: {
    backgroundColor: '#f8fafc',
  },
  alertBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    marginBottom: '20px',
    padding: '14px 16px',
    borderRadius: '10px',
    backgroundColor: '#fffbeb',
    border: '1px solid #fcd34d',
    color: '#92400e',
    fontSize: '14px',
    lineHeight: 1.5,
  },
  alertIcon: {
    flexShrink: 0,
    fontSize: '18px',
  },
  toplamTutarSection: {
    marginTop: '24px',
    padding: '16px 24px',
    background: '#f8fafc',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '16px',
  },
  toplamTutarLabel: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#334155',
  },
  toplamTutarValue: {
    fontSize: '18px',
    fontWeight: 700,
    color: '#1e293b',
  },
  footer: {
    marginTop: '32px',
    paddingTop: '24px',
  },
  footerLine: {
    height: '1px',
    background: '#e2e8f0',
    marginBottom: '16px',
  },
  footerText: {
    fontSize: '11px',
    color: '#94a3b8',
    textAlign: 'center',
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    fontSize: '15px',
    color: '#64748b',
    fontWeight: 500,
  },
  loadingSpinner: {
    width: '36px',
    height: '36px',
    border: '3px solid #e2e8f0',
    borderTopColor: '#3b82f6',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  errorCard: {
    background: '#ffffff',
    padding: '48px',
    borderRadius: '12px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
    textAlign: 'center',
    maxWidth: '400px',
  },
  errorIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    background: '#fee2e2',
    color: '#dc2626',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px',
    fontSize: '24px',
    fontWeight: 700,
  },
  errorText: {
    fontSize: '15px',
    color: '#64748b',
  },
};
